import React, { memo, useMemo } from "react";
import { Divider, Skeleton, Typography } from "antd/lib";
import type { IProductDetail } from "@/models/product";
import { formatThousands } from "@/helpers";
import TokenPrice from "@/app/components/TokenPrice";
import { useCurrencyContext } from "@/context/CurrencyContext";

const InformationDetails = ({ productDetail, isLoading }: { productDetail?: IProductDetail; isLoading: boolean }) => {
  const { currentCurrency } = useCurrencyContext();

  const gridItems = useMemo(
    () => [
      {
        id: 1,
        items: [
          { name: "Previous Close", value: formatThousands(productDetail?.ohlc?.close, currentCurrency) },
          { name: "Open", value: formatThousands(productDetail?.ohlc?.open, currentCurrency) },
          { name: "High", value: formatThousands(productDetail?.ohlc?.high, currentCurrency) },
          { name: "Low", value: formatThousands(productDetail?.ohlc?.low, currentCurrency) },
        ],
      },
      {
        id: 2,
        items: [
          {
            name: "Day’s Range",
            value: `(${formatThousands(productDetail?.days_range.low, currentCurrency)} - ${formatThousands(
              productDetail?.days_range.high,
              currentCurrency,
            )})`,
          },
          {
            name: "52-week Range",
            value: `(${formatThousands(productDetail?.weeks52_range.low, currentCurrency)} - ${formatThousands(
              productDetail?.weeks52_range.high,
              currentCurrency,
            )})`,
          },
          { name: "Daily Volume", value: formatThousands(productDetail?.volume, currentCurrency) },
          { name: "Market cap", value: formatThousands(productDetail?.market_cap, currentCurrency) },
        ],
      },
      {
        id: 3,
        items: [
          { name: "All-Time High", value: formatThousands(productDetail?.high_price?.price, currentCurrency) },
          { name: "All-Time Low", value: formatThousands(productDetail?.low_price?.price, currentCurrency) },
          { name: "Funding Rate", value: formatThousands(productDetail?.funding_rate, currentCurrency) },
          { name: "Open Interest ", value: formatThousands(productDetail?.open_interest, currentCurrency) },
        ],
      },
      {
        id: 4,
        items: [
          { name: "Exchange Net Flow", value: formatThousands(productDetail?.cex_flow?.net, currentCurrency) },
          { name: "Total Holder", value: formatThousands(productDetail?.total_holder) },
          { name: "1w Target est", value: formatThousands(productDetail?.target_1w, currentCurrency) },
          { name: "1y Target est", value: formatThousands(productDetail?.target_1y, currentCurrency) },
        ],
      },
    ],
    [
      currentCurrency,
      productDetail?.cex_flow?.net,
      productDetail?.days_range.high,
      productDetail?.days_range.low,
      productDetail?.funding_rate,
      productDetail?.high_price?.price,
      productDetail?.low_price?.price,
      productDetail?.market_cap,
      productDetail?.ohlc?.close,
      productDetail?.ohlc?.high,
      productDetail?.ohlc?.low,
      productDetail?.ohlc?.open,
      productDetail?.open_interest,
      productDetail?.target_1w,
      productDetail?.target_1y,
      productDetail?.total_holder,
      productDetail?.volume,
      productDetail?.weeks52_range.high,
      productDetail?.weeks52_range.low,
    ],
  );
  return isLoading ? (
    <div className="w-full">
      <Skeleton paragraph={{ rows: 7 }} />
    </div>
  ) : (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-2">
      {gridItems?.map(grid => (
        <div className="flex flex-col gap-2" key={grid.id}>
          {grid.items.map((item, index) => (
            <div key={item.name + index}>
              <div className="flex justify-between">
                <Typography.Text>{item.name}</Typography.Text>
                {item.name === "Previous Close" ? (
                  <TokenPrice
                    initPrice={productDetail?.ohlc?.close ?? 0}
                    symbol={productDetail?.symbol ?? ""}
                    className="font-normal text-black"
                  />
                ) : (
                  <Typography.Text strong>{item.value}</Typography.Text>
                )}
              </div>
              {index < 3 && <Divider style={{ borderColor: "#000000" }} dashed className="w-full my-2 text-black" />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default memo(InformationDetails);
